import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ShieldCheckIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import PublicHeader from '../../components/public/PublicHeader';
import paymentService from '../../services/paymentService';

const PLAN_PRICING = {
    Premium: { monthly: 12, biannual: 68, annual: 122 },
    Enterprise: { monthly: 12, biannual: 68, annual: 122 }
};

const Checkout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state || {};
    const plan = state.plan || 'Premium';
    const billingPeriod = state.billingPeriod || 'monthly';
    const total = state.total ?? PLAN_PRICING[plan]?.[billingPeriod] ?? 12;
    const savings = state.savings || 0;
    const addOns = state.addOns || [];
    const userData = state.userData || null;
    // Critical: tenantId/checkoutSessionId link this payment to the
    // pre-created tenant from /api/signup. Without them the webhook
    // treats the payment as a guest checkout and provisions a phantom
    // tenant, leaving the real user inactive forever (B1).
    const tenantId = state.tenantId || null;
    const checkoutSessionId = state.checkoutSessionId || null;

    const [formData, setFormData] = useState({
        email: userData?.email || '',
        name: userData?.name || '',
        phone: userData?.phone || ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const detectedCurrency = paymentService.detectCurrency();

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError(null);
    };

    const validateForm = () => {
        if (!formData.email || !formData.name) {
            setError('Please fill in all required fields');
            return false;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }

        return true;
    };

    const handlePayment = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Format add-ons as array of module IDs for subscription
            const addOnIds = (addOns || []).map(addon => addon.id);

            const response = await paymentService.initializePayment({
                plan,
                billing_period: billingPeriod,
                amount: total,
                currency: detectedCurrency,
                email: formData.email,
                name: formData.name,
                phone: formData.phone || '',
                metadata: {
                    addOns: addOnIds, // Store as 'addOns' (camelCase) for frontend consistency
                    add_ons: addOns || [], // Keep original for backward compatibility
                    savings: savings || 0,
                    // Link this payment to the pre-created tenant so the
                    // webhook activates the right tenant/user instead of
                    // provisioning a phantom one (B1).
                    tenant_id: tenantId,
                    checkout_session_id: checkoutSessionId,
                    facility_name: userData?.facility_name || formData.name,
                }
            });

            if (response.status === 'success' && response.payment_link) {
                // Redirect to the secure checkout page
                window.location.href = response.payment_link;
            } else {
                console.error('Payment initialization failed:', response);
                console.error('Missing or invalid: status =', response?.status, ', payment_link =', response?.payment_link);
                setError(response.message || 'Failed to initialize payment. Please check your network connection.');
                setLoading(false); // Ensure loading is stopped
            }
        } catch (err) {
            console.error('Payment error:', err);
            setError(err.message || 'Failed to process payment. Please try again.');
            setLoading(false); // Ensure loading is stopped
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-sans selection:bg-teal-500/30 overflow-hidden">
            <PublicHeader />

            {/* Ambient Background */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%27200%27%20height=%27200%27%3E%3Cfilter%20id=%27n%27%3E%3CfeTurbulence%20type=%27fractalNoise%27%20baseFrequency=%270.9%27%20numOctaves=%273%27%20stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect%20width=%27100%25%27%20height=%27100%25%27%20filter=%27url(%23n)%27%20opacity=%270.4%27/%3E%3C/svg%3E')] opacity-20 brightness-100 contrast-150"></div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-gradient-to-b from-teal-50/50 via-transparent to-transparent dark:from-teal-900/20"></div>
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute top-40 -left-20 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
            </div>

            <div className="relative pt-32 pb-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                    {/* Left Column: Form */}
                    <div className="lg:col-span-7">
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl border border-white/20 dark:border-gray-700/50 shadow-2xl shadow-teal-500/5 p-8">
                            <div className="mb-8">
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Checkout</h1>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Complete your registration below.</p>
                            </div>

                            <div className="space-y-8">
                                {/* Account Section */}
                                <div className="space-y-4">
                                    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                                        Account Details
                                    </h2>

                                    {/* Pre-filled notice */}
                                    {userData && (
                                        <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-3 flex items-center gap-2">
                                            <CheckCircleIcon className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                                            <p className="text-xs text-teal-700 dark:text-teal-300">
                                                Your account details have been filled in automatically
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Full Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                required
                                                className="w-full rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all text-sm py-2.5"
                                                placeholder="John Doe"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email Address *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                className="w-full rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all text-sm py-2.5"
                                                placeholder="john@example.com"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Phone Number (Optional)</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                className="w-full rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all text-sm py-2.5"
                                                placeholder="+256 XXX XXX XXX"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

                                {/* Payment Info Section */}
                                <div className="space-y-4">
                                    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                                        Payment Information
                                    </h2>

                                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-xl p-4 border border-teal-200 dark:border-teal-800">
                                        <div className="flex items-start gap-3">
                                            <ShieldCheckIcon className="w-5 h-5 text-teal-600 dark:text-teal-400 mt-0.5" />
                                            <div className="flex-1">
                                                <h3 className="text-sm font-bold text-teal-900 dark:text-teal-100 mb-1">
                                                    Secure Payment via DGateway
                                                </h3>
                                                <p className="text-xs text-teal-700 dark:text-teal-300 mb-3">
                                                    You will be securely redirected to our checkout page to complete your payment.
                                                </p>
                                                <div className="flex flex-wrap gap-2 text-[10px] text-teal-600 dark:text-teal-400">
                                                    <span className="flex items-center gap-1">
                                                        <CheckCircleIcon className="w-3 h-3" />
                                                        Card Payments
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <CheckCircleIcon className="w-3 h-3" />
                                                        Mobile Money
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <CheckCircleIcon className="w-3 h-3" />
                                                        Bank Transfer
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <CheckCircleIcon className="w-3 h-3" />
                                                        PayPal
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Currency Info */}
                                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 text-xs text-gray-600 dark:text-gray-400">
                                        <span className="font-medium">Payment Currency:</span> {detectedCurrency}
                                    </div>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
                                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={handlePayment}
                                    disabled={loading}
                                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-sm shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 hover:scale-[1.01] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    {loading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Processing...
                                        </span>
                                    ) : (
                                        `Pay ${paymentService.formatAmount(total, detectedCurrency)}`
                                    )}
                                </button>

                                <div className="flex justify-center items-center gap-2 text-[10px] text-gray-400">
                                    <ShieldCheckIcon className="w-3.5 h-3.5 text-teal-500" />
                                    <span>256-bit SSL Secure Payment</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Order Summary */}
                    <div className="lg:col-span-5">
                        <div className="sticky top-32">
                            <div className="relative overflow-hidden bg-gray-900 rounded-3xl border border-gray-800 shadow-2xl text-white p-8">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

                                <h3 className="relative text-lg font-bold mb-6 flex items-center gap-2">
                                    Order Summary
                                </h3>

                                <div className="relative space-y-4 mb-6 pb-6 border-b border-gray-800">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="font-bold text-base">{plan} Plan</p>
                                            <p className="text-xs text-gray-400 capitalize">{billingPeriod} billing</p>
                                        </div>
                                        <p className="font-bold text-base">{paymentService.formatAmount(total, detectedCurrency)}</p>
                                    </div>

                                    {addOns && addOns.length > 0 && (
                                        <div className="space-y-2 pt-2">
                                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Add-ons</p>
                                            {addOns.map((addon, idx) => (
                                                <div key={idx} className="flex justify-between text-xs text-gray-300">
                                                    <span>{addon.name}</span>
                                                    <span>+{paymentService.formatAmount(addon.price, detectedCurrency)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {savings > 0 && (
                                    <div className="relative bg-teal-500/10 rounded-xl p-3 mb-6 border border-teal-500/20 flex items-center gap-3">
                                        <div className="bg-teal-500/20 p-1 rounded-full">
                                            <CheckCircleIcon className="w-4 h-4 text-teal-400" />
                                        </div>
                                        <span className="text-xs text-teal-300 font-medium">You're saving {paymentService.formatAmount(savings, detectedCurrency)} with this plan!</span>
                                    </div>
                                )}

                                <div className="relative flex justify-between items-center text-xl font-bold mb-8">
                                    <span>Total</span>
                                    <span>{paymentService.formatAmount(total, detectedCurrency)}</span>
                                </div>

                                <div className="relative space-y-3">
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                        <CheckCircleIcon className="w-3.5 h-3.5 text-teal-500/70" />
                                        <span>30-day money-back guarantee</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                        <CheckCircleIcon className="w-3.5 h-3.5 text-teal-500/70" />
                                        <span>Cancel anytime</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                        <CheckCircleIcon className="w-3.5 h-3.5 text-teal-500/70" />
                                        <span>Instant access</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Checkout;
